import { networkTypeSchema } from './network';
import { z } from 'zod';
import { ChecksumAddressSchema } from './address';
import { isUint256 } from './solidity';

/**
 * Status values used by the Figma transaction simulation states.
 *
 * Numeric values are part of the backend/frontend response contract. Failure
 * variants that this transaction-builder flow cannot emit are omitted.
 */
export enum SimulationStatus {
  SIMULATION_FAILED = 0,
  TRANSACTION_SUCCEEDED = 1,
  TRANSACTION_REVERTED = 2,
}

/** Serialized event value returned by the simulation backend. */
const decodedParameterSchema = z.object({ value: z.string() }).strict();

const wrappedDecodedParameterSchema = z
  .object({ value: decodedParameterSchema })
  .strict();
export type WrappedDecodedParameter = z.infer<
  typeof wrappedDecodedParameterSchema
>;

/** Raw event data returned by the simulation backend. */
const logRawSchema = z.object({ address: ChecksumAddressSchema }).strict();

/** A supported ERC-20 event returned by the simulation endpoint. */
const eventLogSchema = z
  .object({
    name: z.enum(['Transfer', 'Approval']),
    inputs: z.tuple([
      wrappedDecodedParameterSchema,
      wrappedDecodedParameterSchema,
      wrappedDecodedParameterSchema,
    ]),
    raw: logRawSchema,
  })
  .strict();
export type EventLog = z.infer<typeof eventLogSchema>;

/** Native balance values used by the Figma summary. */
const simulationBalanceDiffRawSchema = z
  .object({
    address: ChecksumAddressSchema,
    original: z.string(),
    dirty: z.string(),
    isMiner: z.boolean().optional(),
  })
  .strict();

const simulationBalanceDiffSchema = z
  .object({ raw: z.array(simulationBalanceDiffRawSchema) })
  .strict();

/**
 * Strict subset of the decoded simulation response consumed by this UI.
 *
 * Tenderly's much larger response is converted by the backend. Generic state
 * changes, nonce changes, and fields not rendered by the Figma sheet stay out
 * of this contract.
 */
const simulationResultSchema = z
  .object({
    status: z.enum(SimulationStatus),
    revertString: z.string().optional(),
    gasUsed: z.number().optional(),
    eventLogs: z.array(eventLogSchema).optional(),
    balanceDiffs: z.array(simulationBalanceDiffSchema).optional(),
  })
  .strict();
export type SimulationResult = z.infer<typeof simulationResultSchema>;

/** An address and optional human-readable label used when rendering simulation activity. */
const simulationPartySchema = z
  .object({
    address: ChecksumAddressSchema,
    label: z.string().optional(),
  })
  .strict();
export type SimulationParty = z.infer<typeof simulationPartySchema>;

/** Asset metadata required to format native and ERC-20 simulation values. */
const simulationAssetSchema = z
  .object({
    kind: z.enum(['native', 'erc20']),
    address: ChecksumAddressSchema.optional(),
    symbol: z.string(),
    decimals: z.number(),
    logoUrl: z.string().optional(),
  })
  .strict();
export type SimulationAsset = z.infer<typeof simulationAssetSchema>;

/**
 * Address and asset metadata resolved alongside the raw simulation result.
 * Record keys are normalized lowercase addresses.
 */
const simulationResolutionContextSchema = z
  .object({
    simulatedAccountAddress: ChecksumAddressSchema,
    nativeAsset: simulationAssetSchema,
    assetsByAddress: z.record(z.string(), simulationAssetSchema),
    partiesByAddress: z.record(z.string(), simulationPartySchema),
  })
  .strict();
export type SimulationResolutionContext = z.infer<
  typeof simulationResolutionContextSchema
>;

/** Strict response contract returned by the transaction-builder simulation endpoint. */
export const transactionSimulationResponseSchema = z
  .object({
    result: simulationResultSchema,
    resolution: simulationResolutionContextSchema,
  })
  .strict();
export type TransactionSimulationResponse = z.infer<
  typeof transactionSimulationResponseSchema
>;

/** Child payload; simulation takes its source account and network from the parent. */
export const accountTransactionSimulationChildSchema = z.object({
  policyId: z.uuidv4(),
  to: ChecksumAddressSchema,
  value: z
    .string()
    .regex(/^\d+$/, 'Value must be an unsigned integer string')
    .refine(isUint256, {
      message: 'Value must fit in an unsigned 256-bit integer',
    }),
  data: z
    .string()
    .regex(/^0x(?:[0-9a-fA-F]{2})*$/, 'Data must be byte-aligned hex')
    .refine((data) => data === '0x' || data.length >= 10, {
      message: 'Data must be 0x or contain a 4-byte function selector',
    }),
});

export const accountTransactionSimulationRequestSchema = z.object({
  multichainAccountId: z.uuidv4(),
  networkId: networkTypeSchema,
  children: z
    .tuple([accountTransactionSimulationChildSchema])
    .rest(accountTransactionSimulationChildSchema),
});
export type AccountTransactionSimulationRequest = z.infer<
  typeof accountTransactionSimulationRequestSchema
>;

export type AccountTransactionSimulationChild = z.infer<
  typeof accountTransactionSimulationChildSchema
>;
